import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    LogOut, GraduationCap, ScrollText, MessageSquare, Clock,
    CheckCircle2, AlertCircle, ClipboardCheck, PlayCircle,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

function daysUntil(iso) {
    if (!iso) return null;
    const d = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
    return d;
}

// A render is "usable" if it has a finite duration > 5 seconds. Anything
// less (or 0 / NaN / undefined) is treated as broken and we surface a
// fallback so the learner can still open the knowledge check.
function isRenderUsable(render) {
    if (!render) return false;
    const d = Number(render.duration_s);
    return Number.isFinite(d) && d > 5;
}

function Section({ title, subtitle, items, empty, action }) {
    return (
        <section className="mb-10" data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="mb-4 flex items-baseline justify-between">
                <div>
                    <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
                    {subtitle && <p className="text-sm text-neutral-500">{subtitle}</p>}
                </div>
                {action}
            </div>
            {items.length === 0 ? (
                <Card><CardContent className="py-10 text-sm text-neutral-500 text-center">{empty}</CardContent></Card>
            ) : (
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {items.map((a) => <AssignmentCard key={a.id} a={a} />)}
                </div>
            )}
        </section>
    );
}

function AssignmentCard({ a }) {
    const nav = useNavigate();
    const due = daysUntil(a.due_at);
    const isDone = a.status === "COMPLETED" || a?.progress?.passed;
    const pct = a?.progress?.watch_percentage || 0;
    const bestScore = a?.progress?.best_score;
    // Broken video: render_status FAILED, or no render, or render duration is not finite.
    const renderBroken = (
        a.render_status === "FAILED"
        || (a.published_render && !isRenderUsable(a.published_render))
    );
    return (
        <Card
            className="hover:border-neutral-700 transition-colors flex flex-col"
            data-testid={`assignment-card-${a.id}`}
        >
            <CardContent className="p-5 flex-1 flex flex-col">
                <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="font-medium truncate">{a.module_name || "Untitled module"}</div>
                        <div className="text-xs text-neutral-500 mt-0.5">
                            Assigned {new Date(a.assigned_at).toLocaleDateString()}
                        </div>
                    </div>
                    {isDone ? (
                        <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">
                            <CheckCircle2 size={12} className="mr-1" />Completed
                        </Badge>
                    ) : a.status === "IN_PROGRESS" ? (
                        <Badge variant="secondary">In progress</Badge>
                    ) : due !== null && due < 0 ? (
                        <Badge variant="destructive"><AlertCircle size={12} className="mr-1" />Overdue</Badge>
                    ) : (
                        <Badge variant="outline">Not started</Badge>
                    )}
                </div>

                {!isDone && (
                    <div className="mb-3">
                        <div className="h-1.5 rounded bg-neutral-800 overflow-hidden">
                            <div className="h-full bg-neutral-100" style={{ width: `${pct}%` }} data-testid={`progress-bar-${a.id}`} />
                        </div>
                        <div className="text-xs text-neutral-500 mt-1.5">{pct.toFixed(0)}% watched</div>
                    </div>
                )}

                {isDone && bestScore != null && (
                    <div className="text-sm mb-3">
                        Score <span className="font-semibold">{bestScore.toFixed(0)}%</span>
                    </div>
                )}

                {a.due_at && !isDone && (
                    <div className="text-xs text-neutral-500 flex items-center gap-1 mb-3">
                        <Clock size={12} />Due {new Date(a.due_at).toLocaleDateString()}
                    </div>
                )}

                {renderBroken && !isDone && (
                    <div className="text-[11px] text-amber-300/90 bg-amber-950/40 border border-amber-900/60 rounded px-2 py-1.5 mb-3" data-testid={`assignment-video-broken-${a.id}`}>
                        We're putting the finishing touches on this video. Take the
                        knowledge check now and revisit the video shortly.
                    </div>
                )}

                <div className="mt-auto flex flex-wrap gap-2">
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={(e) => { e.preventDefault(); nav(`/learn/modules/${a.id}`); }}
                        data-testid={`open-module-${a.id}`}
                    >
                        <PlayCircle size={14} className="mr-1" />
                        {isDone ? "Review" : "Open module"}
                    </Button>
                    {renderBroken ? (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.preventDefault(); nav(`/learn/modules/${a.id}/quiz`); }}
                            data-testid="quiz-open-fallback"
                        >
                            <ClipboardCheck size={14} className="mr-1" />
                            Open quiz anyway
                        </Button>
                    ) : (
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.preventDefault(); nav(`/learn/modules/${a.id}/quiz`); }}
                            data-testid={`open-quiz-${a.id}`}
                        >
                            <ClipboardCheck size={14} className="mr-1" />
                            Open Knowledge Check
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

export default function LearnerDashboard() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [items, setItems] = useState(null);

    useEffect(() => {
        api.get("/assignments?mine=true").then((r) => setItems(r.data));
    }, []);

    if (items === null) return <div className="p-8 text-neutral-500">Loading…</div>;

    const inProgress = items.filter((i) => i.status === "IN_PROGRESS" && !i?.progress?.passed);
    const notStarted = items.filter((i) => i.status === "NOT_STARTED");
    const dueSoon = items.filter((i) =>
        i.due_at && daysUntil(i.due_at) !== null && daysUntil(i.due_at) <= 7 && !i?.progress?.passed
    );
    const completed = items.filter((i) => i.status === "COMPLETED" || i?.progress?.passed);

    return (
        <div className="min-h-screen bg-neutral-950 text-neutral-100">
            <header className="border-b border-neutral-800 bg-neutral-950/80 backdrop-blur sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2 font-semibold tracking-tight">
                        <GraduationCap size={18} /> My Learning
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/learn/certificates" className="text-sm text-neutral-400 hover:text-white flex items-center gap-1" data-testid="nav-my-certificates">
                            <ScrollText size={14} /> Certificates
                        </Link>
                        <Button size="sm" variant="ghost" onClick={() => nav("/ai/ask")} className="text-neutral-300 hover:text-white" data-testid="nav-ask-ai">
                            <MessageSquare size={14} className="mr-1" /> Ask AI
                        </Button>
                        <div className="text-sm text-neutral-400">{user?.name}</div>
                        <Button size="sm" variant="ghost" onClick={() => { logout(); nav("/login"); }} data-testid="btn-logout">
                            <LogOut size={14} />
                        </Button>
                    </div>
                </div>
            </header>
            <main className="max-w-6xl mx-auto px-6 py-8">
                <h1 className="text-3xl font-semibold tracking-tight mb-1">
                    Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
                </h1>
                <p className="text-neutral-400 mb-8">Pick up where you left off, or start something new.</p>
                <Section title="Continue Learning" subtitle="Recently active" items={inProgress} empty="Nothing in progress." />
                <Section title="Assigned to Me" items={notStarted} empty="No new assignments right now." />
                <Section title="Due Soon" subtitle="Within 7 days" items={dueSoon} empty="Nothing due in the next week." />
                <Section title="Completed" items={completed} empty="No completions yet." />
            </main>
        </div>
    );
}
